import { z } from "zod";
import { trimmed, uuid } from "./common";

/**
 * Свой бренд вместо Coca (`addon.white_label`, ТЗ 03 §3.1): название, логотип и основной цвет — в ERP, на сайте, в письмах.
 * Цвет — только из палитры: контраст белого текста на кнопке и самого цвета на тёмном фоне — не хуже стандартного оранжевого.
 * Значения — HSL без `hsl()`, как токены `--primary*` в `@cafe/ui/styles/theme.css`.
 */
export const BRAND_COLORS = {
  orange: { h: 22, s: 100, l: 50 },
  red: { h: 0, s: 72, l: 45 },
  rose: { h: 336, s: 72, l: 44 },
  violet: { h: 262, s: 58, l: 52 },
  blue: { h: 217, s: 80, l: 47 },
  teal: { h: 182, s: 82, l: 28 },
  green: { h: 145, s: 63, l: 32 },
  brown: { h: 25, s: 45, l: 36 },
} as const;
export type BrandColor = keyof typeof BRAND_COLORS;
export const BRAND_COLOR_CODES = Object.keys(BRAND_COLORS) as BrandColor[];

/** Токены `--primary*` для цвета бренда: `{ light, dark }` → CSS-переменные. Оранжевый — значения темы по умолчанию. */
export function brandCssVars(color: BrandColor): { light: Record<string, string>; dark: Record<string, string> } {
  const { h, s, l } = BRAND_COLORS[color];
  return {
    light: { "--primary": `${h} ${s}% ${l}%`, "--primary-hover": `${h} ${s}% ${l + 8}%`, "--primary-soft": `${h} ${s}% 96%`, "--primary-soft-hover": `${h} ${Math.min(s, 90)}% 90%` },
    dark: { "--primary": `${h} ${s}% ${Math.max(l + 4, 34)}%`, "--primary-hover": `${h} ${s}% ${Math.max(l + 12, 42)}%`, "--primary-soft": `${h} ${Math.min(s, 60)}% 13%`, "--primary-soft-hover": `${h} ${Math.min(s, 60)}% 18%` },
  };
}

/** CSS для `<style>`: свой цвет поверх темы (и для `data-theme="dark"`). */
export function brandCss(color: BrandColor | null | undefined): string {
  if (!color || color === "orange") return "";
  const v = brandCssVars(color);
  const decl = (vars: Record<string, string>) => Object.entries(vars).map(([k, x]) => `${k}:${x}`).join(";");
  return `:root{${decl(v.light)}}:root[data-theme="dark"]{${decl(v.dark)}}`;
}

export const brandSchema = z.strictObject({
  /** Как называть систему в шапке, вкладке и письмах (обычно — название заведения или сети) */
  name: trimmed(2, 60).nullable(),
  color: z.enum(BRAND_COLOR_CODES as [BrandColor, ...BrandColor[]]).nullable(),
  logoFileId: uuid.nullable(),
});
export type BrandInput = z.input<typeof brandSchema>;
export type BrandSettings = z.output<typeof brandSchema>;
